import { useState } from 'react'
import { Box, Flex, Heading, Button, Badge, Text, Card } from '@chakra-ui/react'
import { Plus, AlertCircle, Gauge } from 'lucide-react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDistributionKeys, useFiscalYears, useCountMissingReadings } from '../hooks'
import { MeterReadingsTable } from '../components/MeterReadingsTable'
import { MeterReadingModal } from '../components/MeterReadingModal'
import { METER_TYPE_LABELS, type DistributionKey } from '../types'

export function MeterReadingsPage() {
  const { residenceId } = useParams<{ residenceId: string }>()
  const navigate = useNavigate()

  const { data: keys } = useDistributionKeys(residenceId)
  const { data: fiscalYears } = useFiscalYears(residenceId)

  // Exercice actif par défaut
  const activeFiscalYear = fiscalYears?.find(fy => fy.status === 'open') || fiscalYears?.[0]

  const variableKeys = keys?.filter(k => k.calculation_base === 'variable') || []

  const [selectedKeyId, setSelectedKeyId] = useState<string>()
  const [isModalOpen, setIsModalOpen] = useState(false)

  if (!residenceId) return null

  if (variableKeys.length === 0) {
    return (
      <Box p={6}>
        <Flex align="center" gap={3} mb={4}>
          <AlertCircle size={24} color="orange" />
          <Heading size="md">Aucune clé variable configurée</Heading>
        </Flex>
        <Text color="gray.600" mb={4}>
          Pour saisir des relevés de compteurs, vous devez d'abord créer une clé de répartition variable.
        </Text>
        <Button
          colorPalette="brand"
          onClick={() => navigate(`/residences/${residenceId}/accounting/settings`)}
        >
          Créer une clé de répartition
        </Button>
      </Box>
    )
  }

  return (
    <Box p={6}>
      <Flex justify="space-between" align="center" mb={6}>
        <Flex align="center" gap={3}>
          <Gauge size={28} />
          <Heading size="lg">Relevés de compteurs</Heading>
        </Flex>
      </Flex>

      {/* Liste des clés variables */}
      {variableKeys.map(key => (
        <MeterKeyCard
          key={key.id}
          distributionKey={key}
          fiscalYearId={activeFiscalYear?.id}
          onAddReading={() => {
            setSelectedKeyId(key.id)
            setIsModalOpen(true)
          }}
        />
      ))}

      {/* Modal de saisie */}
      <MeterReadingModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setSelectedKeyId(undefined)
        }}
        distributionKeyId={selectedKeyId}
        fiscalYearId={activeFiscalYear?.id}
        residenceId={residenceId}
      />
    </Box>
  )
}

// Composant Card pour une clé variable
function MeterKeyCard({
  distributionKey,
  fiscalYearId,
  onAddReading,
}: {
  distributionKey: DistributionKey
  fiscalYearId?: string
  onAddReading: () => void
}) {
  const { data: missingCount } = useCountMissingReadings(
    distributionKey.id,
    fiscalYearId
  )

  return (
    <Card.Root mb={4}>
      <Card.Body>
        <Flex justify="space-between" align="center" mb={4}>
          <Box>
            <Heading size="sm" mb={1}>{distributionKey.name}</Heading>
            <Flex gap={2} align="center">
              <Badge size="sm" colorPalette="blue">
                {METER_TYPE_LABELS[distributionKey.meter_type as keyof typeof METER_TYPE_LABELS]}
              </Badge>
              <Text fontSize="sm" color="gray.600">
                {distributionKey.meter_unit}
              </Text>
              {missingCount && missingCount.missing > 0 && (
                <Badge size="sm" colorPalette="orange">
                  {missingCount.missing} relevé(s) manquant(s)
                </Badge>
              )}
              {missingCount && missingCount.missing === 0 && (
                <Badge size="sm" colorPalette="green">
                  ✓ Tous les relevés saisis
                </Badge>
              )}
            </Flex>
          </Box>
          <Flex gap={2}>
            <Button size="sm" variant="outline" onClick={onAddReading}>
              <Plus size={16} /> Ajouter un relevé
            </Button>
          </Flex>
        </Flex>

        {/* Tableau des relevés pour cette clé */}
        <MeterReadingsTable
          distributionKeyId={distributionKey.id}
          fiscalYearId={fiscalYearId}
        />
      </Card.Body>
    </Card.Root>
  )
}
